/* eslint-disable no-unused-vars */
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { CircularProgress, Table, TableBody, TableContainer, TableHead, TableRow } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import type { ApiDocument } from '@agunity/api-v4';

import AgIconButton from '../../stories/AgIconButton';
import { AgColor } from '../../stories/Styles/AgColor';

import { formatFileSize } from '../../utils';
import { ReactComponent as RemoveIcon } from '../../stories/assets/delete.svg';
import * as S from './styled';

type ContractTableFiles = {
  canEdit?: boolean;
  maxSize: number;
  documents: (ApiDocument & { file?: File })[];
  onView: (doc: ApiDocument) => void;
  onDelete: (doc: ApiDocument) => void;
}

const ContractTableFiles = ({ canEdit, maxSize, documents = [], onView, onDelete }: ContractTableFiles) => {
  const messages = useIntl().formatMessage;
  const [loader, setLoader] = useState<Record<string, boolean>>({});

  const handleDownload = useCallback(async (doc: ApiDocument) => {
    setLoader((prev) => ({ ...prev, [doc.name!]: true }));
    await onView(doc);
    setLoader((prev) => ({ ...prev, [doc.name!]: false }));
  }, [onView]);

  const headers = [{
    field: 'documentName',
    headerName: messages({ id: 'CONTRACTS_FILE_NAME' }),
    width: 165,
  },
  {
    field: 'documentType',
    headerName: messages({ id: 'CONTRACTS_FILE_TYPE' }),
    width: 114,
  },
  {
    field: 'documentSize',
    headerName: messages({ id: 'CONTRACTS_FILE_SIZE' }),
    width: 114,
    align: 'right',
  },
  {
    field: 'actions',
    headerName: '',
    width: 114,
  }];

  const borderStyle = {
    borderColor: AgColor.grey0,
    height: 44,
    width: 44,
    padding: 0
  };

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {headers?.map((header) => (
                <S.StyledTableCell 
                  key={header.field} 
                  width={header.width}
                  sx={{ textAlign: header.field === 'documentSize' ? 'right !important' : '' }} 
                >
                  {header.headerName || ''}
                </S.StyledTableCell>
              ))
            }
          </TableRow>
        </TableHead>
        <TableBody>
          {documents?.map((doc, index) => (
              <TableRow key={index}>
                <S.StyledTableCell sx={{ textAlign: 'left !important' }}>{doc.name}</S.StyledTableCell>
                <S.StyledTableCell sx={{ textAlign: 'left !important' }}>{doc.documentType}</S.StyledTableCell>
                <S.StyledTableCell 
                  sx={{ 
                    textAlign: 'right !important', 
                    fontWeight: (doc?.documentSize || 0) > maxSize ? 600 : 400,
                    color: (doc?.documentSize || 0) > maxSize ? `${AgColor.errorMain} !important` : ''
                  }}
                >
                  {formatFileSize(Number(doc.documentSize || 0))}
                </S.StyledTableCell>
                <S.StyledTableCell isAction>
                  <AgIconButton
                    style={{ display: 'inline-block', margin: '2px' }}
                    buttonStyle={borderStyle}
                    onClick={() => handleDownload(doc)}
                    disabled={loader[doc.name!]}
                    icon={loader[doc.name!] 
                      ? <CircularProgress size={23} /> 
                      : <FileDownloadOutlinedIcon style={{ color: AgColor.link1, height: 24, width: 24, }} />
                    }
                  />
                  {canEdit ? (
                    <AgIconButton
                      style={{ display: 'inline-block', margin: '2px' }}
                      buttonStyle={borderStyle}
                      onClick={() => onDelete(doc)}
                      icon={
                        <S.SVGContainer component='span' iconWidth={24} iconHeight={24} color={AgColor.errorMain}>
                          <RemoveIcon />
                        </S.SVGContainer>
                      }
                    />
                  ) : null}
                </S.StyledTableCell>
              </TableRow>
            ))
          }
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ContractTableFiles;